import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import './Home.css';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import slideOne from '../assets/carousel_1.png';
import slideTwo from '../assets/carousel_2.png';
import slideThree from '../assets/carousel_3.png';
import highlightVideo from '../assets/video.mp4';

const heroHeadlines = [
  'Discover the future of tech',
  'Meet the builders of tomorrow',
  'Where passion meets engineering'
];

const batchHighlights = [
  {
    title: 'Batch 2022 · Seniors',
    description: 'The trailblazers leading campus innovation with advanced capstone projects.',
    to: '/batches?year=2022'
  },
  {
    title: 'Batch 2023 · Sophomores',
    description: 'Honing core skills through hackathons, research labs, and peer mentorship.',
    to: '/batches?year=2023'
  },
  {
    title: 'Batch 2024 · Freshers',
    description: 'Fresh energy tackling real-world problems from day one of campus life.',
    to: '/batches?year=2024'
  }
];

const gallerySlides = [
  { src: slideOne, alt: 'Robotics showcase' },
  { src: slideTwo, alt: 'Hackathon demo day' },
  { src: slideThree, alt: 'Branch collaboration' }
];

const Home = () => {
  const [slideIndex, setSlideIndex] = useState(0);
  const [pauseCarousel, setPauseCarousel] = useState(false);
  const [isMuted, setIsMuted] = useState(true);

  const goToSlide = useCallback((nextIndex) => {
    const total = gallerySlides.length;
    setSlideIndex(((nextIndex % total) + total) % total);
  }, []);

  const handlePrev = () => goToSlide(slideIndex - 1);
  const handleNext = () => goToSlide(slideIndex + 1);

  useEffect(() => {
    if (pauseCarousel) return;
    const id = setInterval(handleNext, 5000);
    return () => clearInterval(id);
  }, [handleNext, pauseCarousel]);

  return (
    <div className="home-page">
      <section className="hero">
        <div className="hero__intro">
          <p className="hero__eyebrow">IIIT Network · College Network</p>
          <h1>Discover the future of tech</h1>
          <p className="hero__description">
            Discover the future of tech with our students. Explore their profiles, skills, and innovative projects. Meet the next generation of engineers, ready to lead and inspire. Welcome to IIIT Network – where talent meets technology.
          </p>
          <div className="hero__actions">
            <Button as={Link} to="/enroll" variant="primary" size="lg">Enroll now</Button>
            <Button as={Link} to="/societies" variant="secondary" size="lg">Tour the societies</Button>
          </div>
        </div>
        <div className="hero__meta">
          <Card className="hero__stat" variant="glass">
            <span className="hero__stat-number">120+</span>
            <span className="hero__stat-label">Active participants</span>
          </Card>
          <Card className="hero__stat" variant="glass">
            <span className="hero__stat-number">35</span>
            <span className="hero__stat-label">Student-led projects</span>
          </Card>
          <Card className="hero__stat" variant="glass">
            <span className="hero__stat-number">15</span>
            <span className="hero__stat-label">Campus societies</span>
          </Card>
        </div>
      </section>

      <section className="batches" aria-labelledby="batch-heading">
        <header>
          <h2 id="batch-heading">Meet the batches</h2>
          <p>Every batch brings a new wave of creativity. Dive into their journeys, achievements, and future-ready skills.</p>
        </header>
        <div className="batch-grid">
          {batchHighlights.map((batch) => (
            <Card
              key={batch.title}
              as={Link}
              to={batch.to}
              className="batch-card"
              hoverEffect
            >
              <span className="batch-card__tag">Explore</span>
              <h3>{batch.title}</h3>
              <p>{batch.description}</p>
              <span className="batch-card__cta">View profiles →</span>
            </Card>
          ))}
        </div>
      </section>

      <section className="gallery" aria-labelledby="gallery-heading">
        <header className="section-heading">
          <h2 id="gallery-heading">Gallery</h2>
          <p>Snapshots from showcase nights, robotics pits, design sprints, and campus celebrations.</p>
        </header>
        <div className="carousel">
          <div className="carousel__track">
            {/* Original slides */}
            {gallerySlides.map((slide) => (
              <figure className="carousel__slide" key={slide.alt}>
                <img src={slide.src} alt={slide.alt} />
                <figcaption>{slide.alt}</figcaption>
              </figure>
            ))}
            {/* Duplicate slides for seamless loop */}
            {gallerySlides.map((slide) => (
              <figure className="carousel__slide" key={`${slide.alt}-dup`}>
                <img src={slide.src} alt={slide.alt} />
                <figcaption>{slide.alt}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="moments" aria-labelledby="moments-heading">
        <header className="section-heading">
          <h2 id="moments-heading">Moments</h2>
          <p>Experience the pulse of IIIT Network through highlights captured by the community.</p>
        </header>
        <div className="moments__media">
          <div className="video-container">
            <video
              autoPlay
              loop
              muted={isMuted}
              playsInline
              poster={gallerySlides[2].src}
            >
              <source src={highlightVideo} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            <button
              className="mute-toggle"
              onClick={() => setIsMuted(!isMuted)}
              aria-label={isMuted ? 'Unmute' : 'Mute'}
            >
              {isMuted ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                  <line x1="23" y1="9" x2="17" y2="15"></line>
                  <line x1="17" y1="9" x2="23" y2="15"></line>
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                  <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path>
                </svg>
              )}
            </button>
          </div>
          <div className="moments__copy">
            <h3>Inside the lab</h3>
            <p>
              From prototyping wearables to building full-stack dashboards, our branches explore diverse domains. Each sprint ends with a showcase where peers, mentors, and alumni exchange feedback and celebrate progress.
            </p>
            <Button as={Link} to="/societies" variant="ghost">Meet society leads →</Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
